import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaSpinner, FaPencilAlt, FaTimes, FaLightbulb, FaSync, FaFileAlt, FaNewspaper, FaDesktop, FaBook } from 'react-icons/fa';
import { supabase } from '../lib/supabase';
import { generateIdeas, expandIdea } from '../api/generate';
import { useAuth } from '../contexts/AuthContext';
import { useModel } from '../contexts/ModelContext';
import { logUserAction } from '../utils/logging';
import Header from './Header';
import ProjectPlan from './ProjectPlan';
import ProjectBacklog from './ProjectBacklog';
import ProjectArtifacts from './ProjectArtifacts';
import GeneratedIdeas from './GeneratedIdeas';
import SelectedIdeas from './SelectedIdeas';
import './Project.css';
import { Project as ProjectType } from '../types';
import { PRFAQGenerator, PRDGenerator, BlogPostGenerator, PresentationGenerator, ResearchPaperGenerator } from '../generators';

interface Idea {
  id: string;
  content: string;
  children: Idea[];
  parent_id: string | null;
  position: number;
  project_id: string;
  user_id: string;
  selected: boolean;
}

interface Artifact {
  id: string;
  type: string;
  content: string;
  created_at: string;
  project_id: string;
  user_id: string;
  version: number;
}

const DEFAULT_TOPICS = [
  "Sustainable Innovation",
  "Digital Wellness",
  "Future of Education",
  "Smart Cities",
  "AI Ethics",
  "Climate Solutions"
];

const Project: React.FC = () => {
  const { id: projectId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { selectedModel } = useModel();
  const [project, setProject] = useState<ProjectType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditingProjectName, setIsEditingProjectName] = useState(false);
  const [editedProjectName, setEditedProjectName] = useState('');
  const [topic, setTopic] = useState('');
  const [generatingIdeas, setGeneratingIdeas] = useState(false);
  const [refreshingTopics, setRefreshingTopics] = useState(false);
  const [ideas, setIdeas] = useState<Idea[]>([]);
  const [topics, setTopics] = useState<string[]>(DEFAULT_TOPICS);
  const [expandedIdeas, setExpandedIdeas] = useState<Record<string, string | null>>({});
  const [expandingIdea, setExpandingIdea] = useState<string | null>(null);
  const [selectedIdeas, setSelectedIdeas] = useState<Set<string>>(new Set());
  const [generatingSimilar, setGeneratingSimilar] = useState<string | null>(null);
  const [rewritingIdea, setRewritingIdea] = useState<string | null>(null);
  const [deletingIdea, setDeletingIdea] = useState<string | null>(null);
  const [regeneratingIdea, setRegeneratingIdea] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'generated' | 'selected' | 'artifacts' | 'plan' | 'backlog'>('generated');
  const [isGeneratePanelOpen, setIsGeneratePanelOpen] = useState(true);
  const [generatingArtifact, setGeneratingArtifact] = useState<string | null>(null);
  const [artifacts, setArtifacts] = useState<Artifact[]>([]);
  const [editingArtifact, setEditingArtifact] = useState<string | null>(null);
  const [editedArtifactContent, setEditedArtifactContent] = useState('');
  const [deletingArtifact, setDeletingArtifact] = useState<string | null>(null);

  const buildIdeaHierarchy = (ideas: any[]): Idea[] => {
    const idMap = new Map<string, Idea>();
    const rootIdeas: Idea[] = [];

    ideas.forEach(idea => {
      idMap.set(idea.id, {
        ...idea,
        children: [],
      });
    });

    ideas.forEach(idea => {
      const ideaWithChildren = idMap.get(idea.id)!;
      if (idea.parent_id === null) {
        rootIdeas.push(ideaWithChildren);
      } else {
        const parent = idMap.get(idea.parent_id);
        if (parent) {
          parent.children.push(ideaWithChildren);
        }
      }
    });

    const sortIdeasByPosition = (ideas: Idea[]) => {
      ideas.sort((a, b) => a.position - b.position);
      ideas.forEach(idea => {
        if (idea.children.length > 0) {
          sortIdeasByPosition(idea.children);
        }
      });
    };

    sortIdeasByPosition(rootIdeas);
    return rootIdeas;
  };

  const findParentChain = (ideas: Idea[], targetId: string): string[] => {
    const chain: string[] = [];
    
    const findParent = (currentIdeas: Idea[], id: string): boolean => {
      for (const idea of currentIdeas) {
        if (idea.id === id) {
          chain.push(idea.id);
          return true;
        }
        if (idea.children.length > 0) {
          if (findParent(idea.children, id)) {
            chain.push(idea.id);
            return true;
          }
        }
      }
      return false;
    };

    findParent(ideas, targetId);
    return chain;
  };

  const findIdeaById = (ideas: Idea[], id: string): Idea | null => {
    for (const idea of ideas) {
      if (idea.id === id) return idea;
      if (idea.children.length > 0) {
        const found = findIdeaById(idea.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const getIdeaChain = (idea: Idea | null): string => {
    if (!idea) return '';
    
    let chain = idea.content;
    const parent = ideas.find(i => i.id === idea.parent_id);
    if (parent) {
      chain = `${getIdeaChain(parent)} > ${chain}`;
    }
    return chain;
  };

  const updateIdeaInTree = (ideas: Idea[], ideaId: string, updates: Partial<Idea>): Idea[] => {
    return ideas.map(idea => {
      if (idea.id === ideaId) {
        return { ...idea, ...updates };
      }
      if (idea.children && idea.children.length > 0) {
        return {
          ...idea,
          children: updateIdeaInTree(idea.children, ideaId, updates),
        };
      }
      return { ...idea, children: idea.children || [] };
    });
  };

  const updateIdeaContent = (ideas: Idea[], ideaId: string, newContent: string): Idea[] => {
    return ideas.map(idea => {
      if (idea.id === ideaId) {
        return { ...idea, content: newContent };
      }
      if (idea.children.length > 0) {
        return { ...idea, children: updateIdeaContent(idea.children, ideaId, newContent) };
      }
      return idea;
    });
  };

  useEffect(() => {
    const loadProjectAndIdeas = async () => {
      try {
        if (!user) {
          throw new Error('User not authenticated');
        }

        const { data: projectData, error: projectError } = await supabase
          .from('projects')
          .select('*')
          .eq('id', projectId)
          .single();

        if (projectError) throw projectError;
        if (!projectData) throw new Error('Project not found');

        setProject(projectData);
        setEditedProjectName(projectData.name);

        const { data: ideasData, error: ideasError } = await supabase
          .from('ideas')
          .select('*')
          .eq('project_id', projectId)
          .order('position', { ascending: true });

        if (ideasError) throw ideasError;

        // Get the latest version of each artifact type
        const { data: artifactsData, error: artifactsError } = await supabase
          .from('artifacts')
          .select('*')
          .eq('project_id', projectId)
          .order('version', { ascending: false });

        if (artifactsError) throw artifactsError;

        // Filter to keep only the latest version of each type
        const latestArtifacts = artifactsData?.reduce((acc: Artifact[], curr) => {
          const existingIndex = acc.findIndex(a => a.type === curr.type);
          if (existingIndex === -1) {
            acc.push(curr);
          }
          return acc;
        }, []) || [];

        setArtifacts(latestArtifacts);

        const hierarchicalIdeas = buildIdeaHierarchy(ideasData || []);
        setIdeas(hierarchicalIdeas);

        // Set initial selected ideas including parent chains
        const selectedIdeaIds = new Set<string>();
        ideasData?.forEach(idea => {
          if (idea.selected) {
            const parentChain = findParentChain(hierarchicalIdeas, idea.id);
            parentChain.forEach(id => selectedIdeaIds.add(id));
          }
        });
        setSelectedIdeas(selectedIdeaIds);
      } catch (error) {
        console.error('Error loading project:', error);
        setError('Failed to load project');
      } finally {
        setLoading(false);
      }
    };

    if (projectId) {
      loadProjectAndIdeas();
    }
  }, [projectId, user]);

  useEffect(() => {
    // Auto-collapse generate panel when ideas exist
    if (ideas.length > 0) {
      setIsGeneratePanelOpen(false);
    }
  }, [ideas.length]);

  const handleUpdateProjectName = async () => {
    if (!project || !editedProjectName.trim()) return;

    try {
      const { error } = await supabase
        .from('projects')
        .update({ name: editedProjectName.trim() })
        .eq('id', project.id);

      if (error) throw error;

      await logUserAction(projectId, user.id, 'update_project_name', { 
        oldName: project?.name,
        newName: editedProjectName 
      });

      setProject({ ...project, name: editedProjectName.trim() });
      setIsEditingProjectName(false);
    } catch (error) {
      console.error('Error updating project name:', error);
      setError('Failed to update project name');
    }
  };

  const handleGenerateIdeas = async () => {
    if (!user || !projectId || !topic.trim()) {
      setError('Please enter a topic first');
      return;
    }

    try {
      setGeneratingIdeas(true);
      setError(null);
      
      const generatedIdeas = await generateIdeas(topic, selectedModel);
      if (!generatedIdeas || generatedIdeas.length === 0) {
        throw new Error('No ideas were generated');
      }

      // Get the current max position
      const { data: maxPositionResult } = await supabase
        .from('ideas')
        .select('position')
        .eq('project_id', projectId)
        .order('position', { ascending: false })
        .limit(1);

      const startPosition = (maxPositionResult?.[0]?.position || 0) + 1;
      
      const { data: newIdeas, error: dbError } = await supabase
        .from('ideas')
        .insert(generatedIdeas.map((content, index) => ({
          content,
          project_id: projectId,
          user_id: user.id,
          position: startPosition + index
        })))
        .select();

      if (dbError) {
        throw new Error(`Database error: ${dbError.message}`);
      }

      if (!newIdeas) {
        throw new Error('Failed to save ideas to database');
      }

      await logUserAction(projectId, user.id, 'generate_ideas', { 
        topic, 
        count: generatedIdeas.length 
      });

      setIdeas(prev => [...newIdeas, ...prev]);
      setTopic('');
      setIsGeneratePanelOpen(false);
    } catch (error) {
      console.error('Error generating ideas:', error);
      setError(
        error instanceof Error 
          ? error.message 
          : 'Failed to generate ideas. Please try again.'
      );
    } finally {
      setGeneratingIdeas(false);
    }
  };

  const handleRefreshTopics = async () => {
    setRefreshingTopics(true);
    try {
      const newTopics = await generateIdeas('Generate 6 unique and creative topics for ideation.', selectedModel);
      if (!newTopics || newTopics.length === 0) {
        throw new Error('No topics were generated');
      }
      setTopics(newTopics);
    } catch (error) {
      console.error('Error refreshing topics:', error);
      setError('Failed to refresh topics. Using default topics.');
      setTopics(DEFAULT_TOPICS);
    } finally {
      setRefreshingTopics(false);
    }
  };

  const handleExpandIdea = async (ideaId: string) => {
    if (!user || !projectId) return;

    try {
      setExpandingIdea(ideaId);
      const ideaToExpand = findIdeaById(ideas, ideaId);
      
      if (!ideaToExpand) {
        throw new Error('Idea not found');
      }

      console.log('Expanding idea:', ideaToExpand.content);
      const expandedContent = await expandIdea(ideaToExpand.content, selectedModel);
      console.log('Expanded content:', expandedContent);
      
      if (!expandedContent) {
        throw new Error('No content generated');
      }

      if (!user || !projectId) {
        throw new Error('User or project not found');
      }

      // Create a new idea with the expanded content
      const { data: newIdea, error } = await supabase
        .from('ideas')
        .insert([
          {
            content: expandedContent,
            project_id: projectId,
            parent_id: ideaId,
            position: ideaToExpand.children.length + 1,
            selected: false,
            user_id: user.id
          }
        ])
        .select()
        .single();

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      if (!newIdea) {
        throw new Error('Failed to create new idea');
      }

      await logUserAction(projectId, user.id, 'expand_idea', { ideaId: ideaId });

      // Update the local state with the new idea
      setIdeas(prevIdeas => {
        const addExpandedIdea = (ideas: Idea[]): Idea[] => {
          return ideas.map(idea => {
            if (idea.id === ideaId) {
              return {
                ...idea,
                children: [...idea.children, { ...newIdea, children: [] }]
              };
            }
            if (idea.children.length > 0) {
              return {
                ...idea,
                children: addExpandedIdea(idea.children)
              };
            }
            return idea;
          });
        };
        return addExpandedIdea(prevIdeas);
      });

    } catch (error) {
      console.error('Error expanding idea:', error);
      setError(error instanceof Error ? error.message : 'Failed to expand idea');
    } finally {
      setExpandingIdea(null);
      // Remove the expandedIdeas entry since we don't need it anymore
      setExpandedIdeas(prev => {
        const newState = { ...prev };
        delete newState[ideaId];
        return newState;
      });
    }
  };

  const handleAddSimilar = async (ideaId: string, content: string) => {
    try {
      if (!projectId || !user) return;

      setGeneratingSimilar(ideaId);
      const generatedContent = await generateIdeas(content, selectedModel);
      
      if (!Array.isArray(generatedContent) || generatedContent.length === 0) {
        throw new Error('No ideas generated');
      }

      // Create all similar ideas in parallel
      const promises = generatedContent.map(async (newContent, index) => {
        const { data, error } = await supabase
          .from('ideas')
          .insert([
            {
              content: newContent,
              project_id: projectId,
              parent_id: ideaId,
              position: index + 1,
              user_id: user.id,
              selected: false
            }
          ])
          .select()
          .single();

        if (error) throw error;
        return data;
      });

      const newIdeas = await Promise.all(promises);

      // Update local state
      setIdeas(prevIdeas => {
        const addSimilarIdeas = (ideas: Idea[]): Idea[] => {
          return ideas.map(idea => {
            if (idea.id === ideaId) {
              return {
                ...idea,
                children: [
                  ...(Array.isArray(idea.children) ? idea.children : []),
                  ...newIdeas.map(newIdea => ({ ...newIdea, children: [] }))
                ]
              };
            }
            if (Array.isArray(idea.children) && idea.children.length > 0) {
              return {
                ...idea,
                children: addSimilarIdeas(idea.children)
              };
            }
            return idea;
          });
        };
        return addSimilarIdeas(prevIdeas);
      });

    } catch (error) {
      console.error('Error adding similar ideas:', error);
      setError('Failed to add similar ideas');
    } finally {
      setGeneratingSimilar(null);
    }
  };

  const handleRewriteIdea = async (ideaId: string, content: string, prompt: string, regenerate: boolean) => {
    try {
      setRewritingIdea(ideaId);
      
      let newContent = content;
      if (regenerate) {
        // Only make API call if regenerating
        const generatedContent = await generateIdeas(`${prompt}: ${content}`, selectedModel);
        if (!generatedContent) {
          throw new Error('Failed to generate rewritten content');
        }
        newContent = generatedContent.split('\n')[0];
        if (!newContent) {
          throw new Error('No valid content was generated');
        }
      }

      setIdeas(prevIdeas => updateIdeaContent(prevIdeas, ideaId, newContent));

    } catch (error) {
      console.error('Error rewriting idea:', error);
      setError('Failed to rewrite idea');
    } finally {
      setRewritingIdea(null);
    }
  };

  const handleRegenerateIdea = async (ideaId: string) => {
    try {
      setRegeneratingIdea(ideaId);
      const ideaToRegenerate = findIdeaById(ideas, ideaId);
      
      if (!ideaToRegenerate) {
        throw new Error('Idea not found');
      }

      const newContent = await generateIdeas(`Generate a new creative idea similar to: ${ideaToRegenerate.content}`, selectedModel);
      
      if (!newContent || newContent.length === 0) {
        throw new Error('No content generated');
      }

      if (!user || !projectId) {
        throw new Error('User or project not found');
      }

      // Update the idea with the new content
      const { error } = await supabase
        .from('ideas')
        .update({ content: newContent[0] })
        .eq('id', ideaId);

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      // Update local state
      setIdeas(prevIdeas => {
        const updateIdea = (ideas: Idea[]): Idea[] => {
          return ideas.map(idea => {
            if (idea.id === ideaId) {
              return { ...idea, content: newContent[0] };
            }
            if (idea.children.length > 0) {
              return { ...idea, children: updateIdea(idea.children) };
            }
            return idea;
          });
        };
        return updateIdea(prevIdeas);
      });

    } catch (error) {
      console.error('Error regenerating idea:', error);
      setError('Failed to regenerate idea. Please try again.');
    } finally {
      setRegeneratingIdea(null);
    }
  };

  const handleDeleteIdea = async (ideaId: string) => {
    try {
      setDeletingIdea(ideaId);

      // Delete from database
      const { error } = await supabase
        .from('ideas')
        .delete()
        .eq('id', ideaId);

      if (error) throw error;

      await logUserAction(projectId, user.id, 'delete_idea', { ideaId: ideaId });

      // Update local state after successful deletion
      setIdeas(prevIdeas => {
        const deleteIdeaAndChildren = (ideas: Idea[]): Idea[] => {
          return ideas.filter(idea => {
            if (idea.id === ideaId) return false;
            if (Array.isArray(idea.children) && idea.children.length > 0) {
              idea.children = deleteIdeaAndChildren(idea.children);
            }
            return true;
          });
        };
        return deleteIdeaAndChildren(prevIdeas);
      });

      // Remove from selected ideas if it was selected
      setSelectedIdeas(prev => {
        const next = new Set(prev);
        next.delete(ideaId);
        return next;
      });

    } catch (error) {
      console.error('Error deleting idea:', error);
      setError('Failed to delete idea');
    } finally {
      setDeletingIdea(null);
    }
  };

  const handleCollapseChildren = (ideaId: string) => {
    setExpandedIdeas(prev => ({ ...prev, [ideaId]: null }));
  };

  const handleSelectIdea = async (ideaId: string) => {
    try {
      if (!user) throw new Error('User not authenticated');

      const idea = findIdeaById(ideas, ideaId);
      if (!idea) throw new Error('Idea not found');

      // Update selected state in database
      const { error } = await supabase
        .from('ideas')
        .update({ selected: !idea.selected })
        .eq('id', ideaId);

      if (error) throw error;

      // Update local state
      const newSelectedIdeas = new Set(selectedIdeas);
      if (idea.selected) {
        newSelectedIdeas.delete(ideaId);
      } else {
        newSelectedIdeas.add(ideaId);
      }
      setSelectedIdeas(newSelectedIdeas);

      // Update idea tree
      setIdeas(prevIdeas => updateIdeaInTree(prevIdeas, ideaId, { selected: !idea.selected }));

      await logUserAction(projectId, user.id, 'select_idea', { ideaId });
    } catch (error) {
      console.error('Error selecting idea:', error);
      setError('Failed to select idea');
    }
  };

  const filterSelectedIdeasWithParents = (ideas: Idea[]): Idea[] => {
    const isInSelectedChain = (idea: Idea): boolean => {
      // Check if this idea or any of its children are selected
      if (idea.selected) return true;
      return idea.children.some(child => isInSelectedChain(child));
    };

    const filterIdeas = (idea: Idea): Idea | null => {
      // If this idea or any of its children are selected
      if (isInSelectedChain(idea)) {
        // Filter children recursively
        const filteredChildren = idea.children
          .map(child => filterIdeas(child))
          .filter((child): child is Idea => child !== null);

        // Return a new idea object with filtered children
        return {
          ...idea,
          children: filteredChildren,
        };
      }
      return null;
    };

    return ideas
      .map(idea => filterIdeas(idea))
      .filter((idea): idea is Idea => idea !== null);
  };

  const countSelectedNodes = (ideas: Idea[]): number => {
    if (!Array.isArray(ideas)) return 0;
    
    let count = 0;
    const countSelected = (currentIdeas: Idea[]) => {
      if (!Array.isArray(currentIdeas)) return;
      
      for (const idea of currentIdeas) {
        if (idea?.selected) {
          count++;
        }
        if (Array.isArray(idea?.children) && idea.children.length > 0) {
          countSelected(idea.children);
        }
      }
    };
    countSelected(ideas);
    return count;
  };

  const getArtifactIcon = (type: string) => {
    switch (type.toLowerCase()) {
      case 'prfaq':
        return <FaFileAlt />;
      case 'prd':
        return <FaPencilAlt />;
      case 'blog':
        return <FaNewspaper />;
      case 'presentation':
        return <FaDesktop />;
      case 'research':
        return <FaBook />;
      default:
        return <FaFileAlt />;
    }
  };

  const handleGenerateArtifact = async (type: string) => {
    if (selectedIdeas.size === 0) return;
    if (!user || !projectId) {
      setError('User not authenticated or project not found');
      return;
    }

    setGeneratingArtifact(type);
    try {
      const selectedIdeaList = Array.from(selectedIdeas)
        .map(id => findIdeaById(ideas, id))
        .filter((idea): idea is Idea => idea !== null);

      // Get the complete context for each selected idea
      const ideaContexts = selectedIdeaList.map(idea => ({
        ...idea,
        context: getIdeaChain(idea)
      }));

      let generator;
      switch (type) {
        case 'prfaq':
          generator = new PRFAQGenerator(ideaContexts, selectedModel);
          break;
        case 'prd':
          generator = new PRDGenerator(ideaContexts, selectedModel);
          break;
        case 'blog':
          generator = new BlogPostGenerator(ideaContexts, selectedModel);
          break;
        case 'presentation':
          generator = new PresentationGenerator(ideaContexts, selectedModel);
          break;
        case 'research':
          generator = new ResearchPaperGenerator(ideaContexts, selectedModel);
          break;
        default:
          throw new Error(`Unknown artifact type: ${type}`);
      }

      const content = await generator.generate();
      
      // Get the latest version number for this type
      const { data: existingArtifacts, error: fetchError } = await supabase
        .from('artifacts')
        .select('*')
        .eq('project_id', projectId)
        .eq('type', type)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      // Find the latest version number, handling cases where version field might not exist
      let nextVersion = 1;
      if (existingArtifacts && existingArtifacts.length > 0) {
        const latestArtifact = existingArtifacts[0];
        nextVersion = (latestArtifact.version || 0) + 1;
      }

      // Create the new artifact
      const newArtifact = {
        type,
        content,
        project_id: projectId,
        user_id: user.id,
        version: nextVersion
      };

      // Save to Supabase
      const { data: savedArtifact, error: saveError } = await supabase
        .from('artifacts')
        .insert([newArtifact])
        .select()
        .single();

      if (saveError) {
        console.error('Error saving artifact:', saveError);
        throw new Error(saveError.message);
      }
      if (!savedArtifact) throw new Error('Failed to save artifact');

      await logUserAction(projectId, user.id, 'generate_artifact', { 
        type,
        artifactId: savedArtifact.id 
      });

      // Update local state
      setArtifacts(prev => [savedArtifact, ...prev]);
      
      // Switch to artifacts tab
      setActiveTab('artifacts');
    } catch (error) {
      console.error('Error generating artifact:', error);
      setError(error instanceof Error ? error.message : 'Failed to generate artifact');
    } finally {
      setGeneratingArtifact(null);
    }
  };

  const handleEditArtifact = async (artifactId: string, content: string) => {
    if (!user || !projectId) return;

    try {
      const { error } = await supabase
        .from('artifacts')
        .update({ content })
        .eq('id', artifactId);

      if (error) throw error;

      await logUserAction(projectId, user.id, 'edit_artifact', { artifactId });

      // Update local state
      setArtifacts(prev => prev.map(a => 
        a.id === artifactId ? { ...a, content } : a
      ));

      // Reset editing state
      setEditingArtifact(null);
      setEditedArtifactContent('');
    } catch (error) {
      console.error('Error updating artifact:', error);
      setError(error instanceof Error ? error.message : 'Failed to update artifact');
    }
  };

  const handleDeleteArtifact = async (artifactId: string) => {
    try {
      setDeletingArtifact(artifactId);
      const { error } = await supabase
        .from('artifacts')
        .delete()
        .eq('id', artifactId);

      if (error) throw error;

      if (user && projectId) {
        await logUserAction(projectId, user.id, 'delete_artifact', { artifactId });
      }

      setArtifacts(artifacts.filter(a => a.id !== artifactId));
    } catch (error) {
      console.error('Error deleting artifact:', error);
    } finally {
      setDeletingArtifact(null);
    }
  };

  const startEditingArtifact = (artifact: Artifact) => {
    setEditingArtifact(artifact.id);
    setEditedArtifactContent(artifact.content);
  };

  const cancelEditingArtifact = () => {
    setEditingArtifact(null);
    setEditedArtifactContent('');
  };

  if (loading) {
    return (
      <div className="project-container">
        <Header />
        <div className="project-loading">
          <FaSpinner className="spinner" />
          <p>Loading project...</p>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="project-container">
        <Header />
        <div className="project-error">
          <p>{error || 'Project not found'}</p>
          <button onClick={() => navigate('/')}>Back to Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="project-container">
      <Header 
        selectedIdeas={selectedIdeas}
        selectedIdeasContent={null}
        projectName={project?.name}
        onEditProjectName={handleUpdateProjectName}
        isEditingProjectName={isEditingProjectName}
        onStartEditing={() => setIsEditingProjectName(true)}
        editedProjectName={editedProjectName}
        onChangeProjectName={setEditedProjectName}
        onCancelEditing={() => {
          setIsEditingProjectName(false);
          setEditedProjectName(project?.name || '');
        }}
        generateButton={
          !isGeneratePanelOpen ? (
            <button
              className="header-generate-button"
              onClick={() => setIsGeneratePanelOpen(true)}
            >
              <FaLightbulb />
              Generate Ideas
            </button>
          ) : null
        }
      />
      <div className="project-content">
        {isGeneratePanelOpen && (
          <div className="generate-panel">
            <div className="panel-header" onClick={() => setIsGeneratePanelOpen(false)}>
              <h3>
                <FaLightbulb style={{ marginRight: '8px' }} />
                Generate Ideas
              </h3>
              <button className="toggle-button">
                <FaTimes />
              </button>
            </div>

            <div className="panel-content">
              <div className="topic-input">
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="Enter a topic for ideation..."
                  onKeyDown={(e) => e.key === 'Enter' && handleGenerateIdeas()}
                />
                <button
                  style={{ backgroundColor: '#4f46e5', color: 'white' }}
                  onClick={handleGenerateIdeas}
                  disabled={generatingIdeas || !topic.trim()}
                >
                  {generatingIdeas ? (
                    <>
                      <FaSpinner className="spinner" />
                      Generating...
                    </>
                  ) : (
                    'Generate'
                  )}
                </button>
              </div>

              <div className="topic-suggestions">
                <div className="suggestions-header">
                  <h4>Quick Topics</h4>
                  <button
                    onClick={handleRefreshTopics}
                    disabled={refreshingTopics}
                    className="refresh-button"
                    title="Refresh topics"
                  >
                    <FaSync className={refreshingTopics ? 'spinner' : ''} />
                  </button>
                </div>
                <div className="topic-buttons">
                  {topics.map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => setTopic(suggestion)}
                      className="topic-button"
                      title={suggestion}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="ideas-tabs">
          <button 
            className={`tab-button ${activeTab === 'generated' ? 'active' : ''}`}
            onClick={() => setActiveTab('generated')}
          >
            Generated Ideas ({ideas.length})
          </button>
          <button 
            className={`tab-button ${activeTab === 'selected' ? 'active' : ''}`}
            onClick={() => setActiveTab('selected')}
          >
            Selected Ideas ({countSelectedNodes(ideas)})
          </button>
          <button 
            className={`tab-button ${activeTab === 'artifacts' ? 'active' : ''}`}
            onClick={() => setActiveTab('artifacts')}
          >
            Artifacts ({artifacts.length})
          </button>
          <button 
            className={`tab-button ${activeTab === 'plan' ? 'active' : ''}`}
            onClick={() => setActiveTab('plan')}
          >
            Plan
          </button>
          <button 
            className={`tab-button ${activeTab === 'backlog' ? 'active' : ''}`}
            onClick={() => setActiveTab('backlog')}
          >
            Backlog
          </button>
        </div>

        <div className="tab-content">
          {activeTab === 'generated' && (
            <GeneratedIdeas
              ideas={ideas}
              onExpand={handleExpandIdea}
              onAddSimilar={handleAddSimilar}
              onSelect={handleSelectIdea}
              onCollapseChildren={handleCollapseChildren}
              onDelete={handleDeleteIdea}
              onRegenerate={handleRegenerateIdea}
              onRewrite={handleRewriteIdea}
              expandedIdeas={expandedIdeas}
              expandingIdea={expandingIdea}
              selectedIdeas={selectedIdeas}
              generatingSimilar={generatingSimilar}
              rewritingIdea={rewritingIdea}
              deletingIdea={deletingIdea}
              regeneratingIdea={regeneratingIdea}
            />
          )}

          {activeTab === 'selected' && (
            <SelectedIdeas
              ideas={ideas}
              selectedIdeas={selectedIdeas}
              onExpand={handleExpandIdea}
              onAddSimilar={handleAddSimilar}
              onSelect={handleSelectIdea}
              onCollapseChildren={handleCollapseChildren}
              onDelete={handleDeleteIdea}
              onRegenerate={handleRegenerateIdea}
              onRewrite={handleRewriteIdea}
              expandedIdeas={expandedIdeas}
              expandingIdea={expandingIdea}
              generatingSimilar={generatingSimilar}
              rewritingIdea={rewritingIdea}
              deletingIdea={deletingIdea}
              regeneratingIdea={regeneratingIdea}
              onGenerateArtifact={handleGenerateArtifact}
              generatingArtifact={generatingArtifact}
              filterSelectedIdeasWithParents={filterSelectedIdeasWithParents}
            />
          )}

          {activeTab === 'artifacts' && (
            <ProjectArtifacts
              artifacts={artifacts}
              onEdit={startEditingArtifact}
              onDelete={handleDeleteArtifact}
              editingArtifact={editingArtifact}
              editedArtifactContent={editedArtifactContent}
              onEditContentChange={(content) => setEditedArtifactContent(content)}
              onSave={handleEditArtifact}
              onCancelEdit={cancelEditingArtifact}
              deletingArtifact={deletingArtifact}
              getArtifactIcon={getArtifactIcon}
            />
          )}

          {activeTab === 'plan' && <ProjectPlan />}

          {activeTab === 'backlog' && <ProjectBacklog />}
        </div>
      </div>
    </div>
  );
};

export default Project;
